import { api } from "@/lib/api";
import {
	artDotColor,
	cn,
	formatCurrency,
	formatDate,
	statusColor,
	statusEmoji,
} from "@/lib/utils";
import {
	BOOKING_ARTS,
	BOOKING_STATUSES,
	type BookingArt,
	type BookingStatus,
} from "@booking-hub/shared";
import { useQuery } from "@tanstack/react-query";
import { Archive, Filter, Plus, Search } from "lucide-react";
import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

// ============================================================
// Bookings Page — Tabelle + Filter
// ============================================================

interface BookingListItem {
	id: string;
	kunde: string;
	firma: string | null;
	datum: string | null;
	art: string | null;
	status: string;
	personen: number | null;
	preis: number | null;
	quelle: string | null;
	kontaktEmail: string | null;
	kontaktTel: string | null;
	angebotGesendet: string | null;
	abGesendet: string | null;
	rechnungGesendet: string | null;
	bezahltAm: string | null;
	aiSuggestedAction: string | null;
}

interface BookingListResponse {
	data: BookingListItem[];
	total: number;
	limit: number;
	offset: number;
}

// ============================================================
// Completeness Dots — compact progress indicator per booking
// ============================================================

function MiniPipeline({ booking }: { booking: BookingListItem }) {
	const steps = [
		{
			label: "AG",
			done: !!booking.angebotGesendet,
			color: "bg-blue-500",
			inactive: "bg-stone-200",
		},
		{
			label: "AB",
			done: !!booking.abGesendet,
			color: "bg-emerald-500",
			inactive: "bg-stone-200",
		},
		{
			label: "RE",
			done: !!booking.rechnungGesendet,
			color: booking.bezahltAm ? "bg-green-500" : "bg-orange-500",
			inactive: "bg-stone-200",
		},
	];

	const tooltip = steps
		.map((s) => `${s.done ? "●" : "○"} ${s.label}`)
		.join("  ");

	return (
		<div className="flex items-center gap-1" title={tooltip}>
			{steps.map((s, i) => (
				<span key={s.label} className="flex items-center gap-1">
					{i > 0 && <span className="w-2 h-px bg-stone-200" />}
					<span
						className={cn(
							"w-2 h-2 rounded-full",
							s.done ? s.color : s.inactive,
						)}
					/>
				</span>
			))}
		</div>
	);
}

export function BookingsPage() {
	const [params, setParams] = useSearchParams();
	const [showFilters, setShowFilters] = useState(false);

	const q = params.get("q") ?? "";
	const status = params.get("status") ?? "";
	const art = params.get("art") ?? "";
	const archived = params.get("archived") === "true";
	const page = Number(params.get("page") ?? "0");

	const query = useQuery({
		queryKey: ["bookings", { q, status, art, archived, page }],
		queryFn: () => {
			const p = new URLSearchParams();
			if (q) p.set("search", q);
			if (status) p.set("status", status);
			if (art) p.set("art", art);
			if (archived) p.set("archived", "true");
			p.set("offset", String(page * 25));
			p.set("limit", "25");
			return api.get<BookingListResponse>(`/bookings?${p.toString()}`);
		},
	});

	const bookings = query.data?.data ?? [];
	const total = query.data?.total ?? 0;

	function setFilter(key: string, value: string) {
		const next = new URLSearchParams(params);
		if (value) next.set(key, value);
		else next.delete(key);
		next.delete("page");
		setParams(next);
	}

	return (
		<div className="space-y-4 animate-fade-in">
			{/* Header */}
			<div className="flex items-center justify-between">
				<div>
					<h1 className="font-display text-2xl">
						{archived ? "Archivierte Buchungen" : "Buchungen"}
					</h1>
					<p className="text-sm text-stone-500">{total} gesamt</p>
				</div>
				<Link to="/bookings/new" className="btn-primary">
					<Plus size={16} /> Neue Buchung
				</Link>
			</div>

			{/* Search + Filter Toggle */}
			<div className="flex gap-3">
				<div className="relative flex-1">
					<Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400" />
					<input
						type="text"
						value={q}
						onChange={(e) => setFilter("q", e.target.value)}
						placeholder="Kunde, Firma suchen..."
						className="input pl-9"
					/>
				</div>
				<button
					type="button"
					onClick={() => setShowFilters(!showFilters)}
					className={cn(
						"btn-secondary",
						showFilters && "bg-brand-50 text-brand-700",
					)}
				>
					<Filter size={16} /> Filter
				</button>
				<button
					type="button"
					onClick={() => setFilter("archived", archived ? "" : "true")}
					className={cn(
						"btn-secondary",
						archived && "bg-amber-50 text-amber-700",
					)}
					title={archived ? "Aktive anzeigen" : "Archivierte anzeigen"}
				>
					<Archive size={16} /> {archived ? "Archiv" : "Archiv"}
				</button>
			</div>

			{/* Filter Bar */}
			{showFilters && (
				<div className="flex flex-wrap gap-3 animate-slide-up">
					<select
						value={status}
						onChange={(e) => setFilter("status", e.target.value)}
						className="input w-auto"
					>
						<option value="">Alle Status</option>
						{BOOKING_STATUSES.map((s) => (
							<option key={s} value={s}>
								{statusEmoji(s)} {s}
							</option>
						))}
					</select>
					<select
						value={art}
						onChange={(e) => setFilter("art", e.target.value)}
						className="input w-auto"
					>
						<option value="">Alle Arten</option>
						{BOOKING_ARTS.map((a) => (
							<option key={a} value={a}>
								{a}
							</option>
						))}
					</select>
				</div>
			)}

			{/* Table */}
			<div className="card overflow-hidden !p-0">
				<div className="overflow-x-auto">
					<table className="w-full text-sm">
						<thead>
							<tr className="border-b border-stone-100 bg-stone-50/50 text-left">
								<th className="px-4 py-3 font-medium text-stone-500">Datum</th>
								<th className="px-4 py-3 font-medium text-stone-500">Kunde</th>
								<th className="px-4 py-3 font-medium text-stone-500">Art</th>
								<th className="px-4 py-3 font-medium text-stone-500">Status</th>
								<th className="px-4 py-3 font-medium text-stone-500 text-center">
									Fortschritt
								</th>
								<th className="px-4 py-3 font-medium text-stone-500 text-right">
									Betrag
								</th>
							</tr>
						</thead>
						<tbody>
							{bookings.map((b) => (
								<tr
									key={b.id}
									className="border-b border-stone-50 hover:bg-stone-50/50 transition-colors"
								>
									<td className="px-4 py-3 font-mono text-xs text-stone-600">
										{formatDate(b.datum)}
									</td>
									<td className="px-4 py-3">
										<Link
											to={`/bookings/${b.id}`}
											className="font-medium text-stone-900 hover:text-brand-600"
										>
											{b.kunde}
										</Link>
										{b.firma && (
											<div className="text-xs text-stone-400">{b.firma}</div>
										)}
									</td>
									<td className="px-4 py-3">
										{b.art && (
											<span className="inline-flex items-center gap-1.5 text-xs">
												<span
													className="h-2 w-2 rounded-full"
													style={{
														backgroundColor: artDotColor(b.art as BookingArt),
													}}
												/>
												{b.art}
											</span>
										)}
									</td>
									<td className="px-4 py-3">
										<span
											className={`badge ${statusColor(b.status as BookingStatus)}`}
										>
											{statusEmoji(b.status as BookingStatus)} {b.status}
										</span>
									</td>
									<td className="px-4 py-3 text-center">
										<MiniPipeline booking={b} />
									</td>
									<td className="px-4 py-3 text-right font-mono text-xs">
										{formatCurrency(b.preis)}
									</td>
								</tr>
							))}
							{bookings.length === 0 && !query.isLoading && (
								<tr>
									<td
										colSpan={6}
										className="px-4 py-12 text-center text-stone-400"
									>
										Keine Buchungen gefunden.
									</td>
								</tr>
							)}
						</tbody>
					</table>
				</div>

				{/* Pagination */}
				{total > 25 && (
					<div className="flex items-center justify-between border-t border-stone-100 px-4 py-3">
						<span className="text-xs text-stone-500">
							Seite {page + 1} von {Math.ceil(total / 25)}
						</span>
						<div className="flex gap-2">
							<button
								type="button"
								disabled={page === 0}
								onClick={() => setFilter("page", String(page - 1))}
								className="btn-ghost text-xs disabled:opacity-30"
							>
								Zurück
							</button>
							<button
								type="button"
								disabled={(page + 1) * 25 >= total}
								onClick={() => setFilter("page", String(page + 1))}
								className="btn-ghost text-xs disabled:opacity-30"
							>
								Weiter
							</button>
						</div>
					</div>
				)}
			</div>
		</div>
	);
}
